import SearchIcon from '@mui/icons-material/Search';
import { Box, InputAdornment, TextField, Typography } from '@mui/material';
import { GridColDef, GridRowParams } from '@mui/x-data-grid-premium';
import { useQuery } from '@tanstack/react-query';
import React, { ChangeEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';

import { TrackedDataGrid } from '../../../components/TrackedComponents';
import useApi from '../../../hooks/api';
import useIsMobile from '../../../hooks/isMobile';
import useProfile from '../../../hooks/profile';
import UniformFolderImages from './UniformFolderImages';
import { formatUniformPrefixForDisplay } from './uniformPrefixDisplay';

function normalizeUniformPrefix(value: string): string {
    // remove trailing slash and return the last segment (removing customer name)
    return value.replace(/\/+$/, '').replace(/.*\//, '');
}

const UniformPrefixList: React.FC = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const { api } = useApi();
    const { profile } = useProfile();
    const isMobile = useIsMobile();
    const [quickSearch, setQuickSearch] = useState('');

    const { data, isLoading, isError } = useQuery({
        queryKey: ['uniform_wellness_images', 'root_keys'],
        queryFn: async () => {
            const res = await api.apiV0CustomerUniformWellnessImagesGet();
            return res.data ?? [];
        },
    });

    const rows = useMemo(
        () =>
            (data ?? []).map((raw, index) => {
                const prefix = normalizeUniformPrefix(raw);
                return {
                    id: index,
                    prefix,
                    displayLabel: formatUniformPrefixForDisplay(prefix, profile),
                };
            }),
        [data, profile],
    );

    const filteredRows = useMemo(() => {
        const q = quickSearch.trim().toLowerCase();
        if (!q) {
            return rows;
        }
        return rows.filter((row) => row.prefix.toLowerCase().includes(q) || row.displayLabel.toLowerCase().includes(q));
    }, [rows, quickSearch]);

    const columns = useMemo<GridColDef[]>(
        () => [
            {
                field: 'displayLabel',
                headerName: t('content.uniform.prefix_column'),
                flex: 1,
                minWidth: 240,
            },
        ],
        [t],
    );

    return (
        <Box
            sx={{
                p: 2,
                height: '100%',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
            }}
        >
            {isError ? (
                <Typography variant="body2" color="error">
                    {t('content.uniform.load_error')}
                </Typography>
            ) : null}
            <TextField
                id="uniform-images-quick-search"
                data-testid="uniform-images-quick-search"
                variant="standard"
                size="small"
                fullWidth
                placeholder={t('table.search')}
                value={quickSearch}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setQuickSearch(e.target.value)}
                disabled={isLoading || isError}
                sx={{ maxWidth: isMobile ? '100%' : 400, mb: 2, mt: 1 }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon fontSize="small" color="action" aria-hidden />
                        </InputAdornment>
                    ),
                }}
            />
            <Box sx={{ flex: 1, minHeight: 360, width: '100%', pt: 0 }}>
                <TrackedDataGrid
                    id="uniform-images-prefix-grid"
                    data-testid="uniform-images-prefix-grid"
                    rows={filteredRows}
                    columns={columns}
                    loading={isLoading}
                    disableRowSelectionOnClick
                    hideFooter
                    disableColumnMenu
                    disableVirtualization={isMobile || process.env.JEST_WORKER_ID !== undefined}
                    onRowClick={(params: GridRowParams) => {
                        const prefix = params.row.prefix as string;
                        if (typeof prefix === 'string' && prefix.length > 0) {
                            history.push(`/uniform_images/${encodeURIComponent(prefix)}`);
                        }
                    }}
                    sx={{
                        height: '100%',
                        width: '100%',
                        '& .MuiDataGrid-row': { cursor: 'pointer' },
                    }}
                />
            </Box>
        </Box>
    );
};

const UniformComponent: React.FC = () => {
    const { prefix } = useParams<{ prefix?: string }>();
    if (prefix !== undefined) {
        return <UniformFolderImages />;
    }
    return <UniformPrefixList />;
};

export default UniformComponent;
